import React, { useEffect, useState, memo, useRef } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useApi } from "../../../../../../shared/hooks/useApi";
import CustomTable from "../../../../../../shared/components/table/custom-table";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import { fetchFacilityDetails } from './facility-setail-api';
import { useParams } from 'react-router-dom';

// Row interface: parameters only (extracted from facilityInfo)
interface FacilityRow {
  facilityId: string;
}

// Props interface for the RenderDetailPanel
interface RenderDetailPanelProps {
  row: { original: { facilityInfo: FacilityRow } };
}

// Column definitions for Facility Information table
const facilityInfoColumns = [
  { accessorKey: 'isMainSite', header: 'Is Facility Main Site', minSize: 60, maxSize: 150 },
  { accessorKey: 'facilityNo', header: 'Facility No.', minSize: 60, maxSize: 150 },
  { accessorKey: 'facilityType', header: 'Facility Type', minSize: 60, maxSize: 150 },
  { accessorKey: 'facilityTypeDescription', header: 'Facility Type Description', minSize: 60, maxSize: 150 },
  // { accessorKey: 'facilityOarId', header: 'Facility OAR ID', minSize: 60, maxSize: 150 },
  { accessorKey: 'lastAuditDate', header: 'Last Audit Date', minSize: 60, maxSize: 150 },
  { accessorKey: 'farmCapacity', header: 'Farm Capacity', minSize: 60, maxSize: 150 },
  { accessorKey: 'farmNoAnimals', header: 'No. Of Animals', minSize: 60, maxSize: 150 },
  { accessorKey: 'iId', header: 'TE-ID', minSize: 60, maxSize: 150 },
  { accessorKey: 'facilityStandard', header: 'Facility Standard', minSize: 60, maxSize: 150 },
  { accessorKey: 'facilityStandardDescription', header: 'Facility Standard Description', minSize: 60, maxSize: 150 },
];

// Column definitions for Address Information table
const addressInfoColumns = [
  { accessorKey: 'name', header: 'Name', minSize: 60, maxSize: 150 },
  { accessorKey: 'address1', header: 'Address 1', minSize: 60, maxSize: 150 },
  // { accessorKey: 'address2', header: 'Address 2', minSize: 60, maxSize: 150 },
  // { accessorKey: 'address3', header: 'Address 3', minSize: 60, maxSize: 150 },
  { accessorKey: 'postcode', header: 'Postcode', minSize: 60, maxSize: 150 },
  { accessorKey: 'town', header: 'Town', minSize: 60, maxSize: 150 },
  { accessorKey: 'countryAreaWithDesc', header: 'Country/Area', minSize: 60, maxSize: 150 },
  { accessorKey: 'stateProvinceWithDesc', header: 'State/Province', minSize: 60, maxSize: 150 },
];

// Column definitions for Certification Information table
const certificationInfoColumns = [
  { accessorKey: 'subcontractorLicenseNo', header: 'Subcontractor License No.', minSize: 60, maxSize: 150 },
  { accessorKey: 'certificationBody', header: 'Certification Body', minSize: 60, maxSize: 150 },
  // { accessorKey: 'expiryDate', header: 'Expiry Date', minSize: 60, maxSize: 150 },
];

// Column definitions for Facility Farm Capacity Information table
const facilityFarmInfoColumns = [
  { accessorKey: 'facilityFarmRawMaterialCode', header: 'Farm Raw Material Code', minSize: 60, maxSize: 150 },
  { accessorKey: 'facilityFarmRawMaterialCodeDescription', header: 'Farm Raw Material Description', minSize: 60, maxSize: 150 },
  { accessorKey: 'facilityFarmRawMaterialCapacity', header: 'Farm Raw Material Capacity', minSize: 60, maxSize: 150 },
  // { accessorKey: 'expiryDate', header: 'Expiry Date', minSize: 60, maxSize: 150 },
];

// Column definitions for Facility Process Information table
const facilityProcessColumns = [
  { accessorKey: 'facilityProcessCategoryCode', header: 'Process Category Code', minSize: 60, maxSize: 150 },
  { accessorKey: 'facilityProcessCategoryCodeDescription', header: 'Process Category Code Description', minSize: 60, maxSize: 150 },
  { accessorKey: 'facilityProcessCategoryUserSpecificTerm', header: 'Process User Specific Term', minSize: 60, maxSize: 150 },
];

const RenderDetailPanel = memo(({ row }: RenderDetailPanelProps) => {
  // Extract API parameters from row.original.facilityInfo
  const { facilityId } = row.original.facilityInfo;
  const { loading, executeGet } = useApi();
  const [facilityData, setFacilityData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);
const {SC_ID}=useParams()
  useEffect(() => {
    if (!SC_ID || !facilityId || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    fetchFacilityDetails(executeGet, SC_ID, facilityId)
      .then((data) => setFacilityData(data?.data))
      .catch((err) => setError(err.message));
  }, [SC_ID, facilityId, executeGet]);

  if (loading) return <LoaderComponent />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!facilityData) return <Typography>Loading facility details...</Typography>;

  // Use the first facility detail object from API response
  const detail = facilityData || {};

  // Map Facility Information fields
  const mappedFacilityInfo = {
    isMainSite: detail.isMainSite || '',
    facilityNo: detail.facilityNo || '',
    facilityType: detail.facilityType || '',
    facilityTypeDescription: detail.facilityTypeDescription || '',
    facilityOarId: detail.facilityOarId || '',
    lastAuditDate: detail.facilityLastAuditDate || '',
    farmCapacity: Array.isArray(detail.facilityFarmCapacity)
      ? detail.facilityFarmCapacity.join(', ')
      : detail.facilityFarmCapacity || '',
    farmNoAnimals: Array.isArray(detail.facilityFarmNoAnimals)
      ? detail.facilityFarmNoAnimals.join(', ')
      : detail.facilityFarmNoAnimals || '',
    iId: detail.facilityTeId || '',
    facilityStandard: Array.isArray(detail.facilityStandard)
      ? detail.facilityStandard.join(', ')
      : detail.facilityStandard || '',
    facilityStandardDescription: Array.isArray(detail.facilityStandardDescription)
    ? detail.facilityStandardDescription.join(', ')
    : detail.facilityStandardDescription || '',
  };

  // Map Address Information fields
  const mappedAddressInfo = {
    name: detail.facilityName || '',
    address1: detail.facilityAddress1 || '',
    // address2: detail.facilityAddress2 || '',
    // address3: detail.facilityAddress3 || '',
    postcode: detail.facilityPostcode || '',
    town: detail.facilityTown || '',
    countryArea: detail.facilityCountryOrArea || '',
    countryAreaWithDesc: detail.facilityCountryOrArea ? detail.facilityCountryOrArea + ' - ' +detail.facilityCountryOrAreaDescription : '',
    stateProvince: detail.facilityStateOrProvince || '',
    stateProvinceWithDesc: detail.facilityStateOrProvince ? detail.facilityStateOrProvince + ' - ' + detail.facilityStateOrProvinceDescription : '',
  };

  // Map Certification Information fields
  const mappedCertificationInfo = {
    subcontractorLicenseNo: detail.facilitySubcontractorLicenseNo || '',
    certificationBody: detail.facilityCbOfIndependentlyCertifiedSubcontractor || '',
    // expiryDate: detail.facilityExpiryDateOfIndependentlyCertifiedSubcontractor || '',
  };

  const mappedFacilityFarmCapacityInfo = detail?.facilityFarmCapacityInformation;

  // Map Facility Process Information from detail.facilityProcessDetails array
  const facilityProcess = detail.facilityProcessInformation || [];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 5,
        gap: 4,
        width: '100%',
      }}
    >
      {/* Facility Information */}
      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Facility Information
        </Typography>
        <CustomTable
          columns={facilityInfoColumns}
          data={[mappedFacilityInfo]}
          enableTopToolbar={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />
      {/* Address Information */}
      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Address Information
        </Typography>
        <CustomTable
          columns={addressInfoColumns}
          data={[mappedAddressInfo]}
          enableTopToolbar={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />
      {/* Certification Information */}
      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Certification Information
        </Typography>
        <CustomTable
          columns={certificationInfoColumns}
          data={[mappedCertificationInfo]}
          enableTopToolbar={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />
      {/* Facility Farm Capacity Information */}
      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Facility Farm Capacity Information
        </Typography>
        <CustomTable
          columns={facilityFarmInfoColumns}
          data={mappedFacilityFarmCapacityInfo}
          enableTopToolbar={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />
      {/* Facility Process Information */}
      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Facility Process Information
        </Typography>
        <CustomTable
          columns={facilityProcessColumns}
          data={facilityProcess}
          enableTopToolbar={false}
          enableBottomToolbar={false}
        />
      </Box>
    </Box>
  );
});

export default RenderDetailPanel;
